import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useSearchParams } from 'react-router';
import { clsx } from '../../../../ds/clsx';
import { Banner, Button, Skeleton, useToast } from '../../../../ds/components';
import { errorMessage } from '../../../../lib/api';
import { OrganizationPage, QueryError } from '../users/OrganizationPage';
import {
  useAddStage,
  usePipelineEditor,
  useRenameStage,
  useReorderStages,
  type PipelineItem,
  type PipelineType,
} from './api';
import { DeleteStageDialog } from './DeleteStageDialog';
import styles from './PipelinesPage.module.css';

const INTRO =
  'Stages mit Pflichtangaben (System) sind fest. Eigene Stages können Sie umbenennen, verschieben und löschen.';

const TYPES: readonly PipelineType[] = ['property', 'financing', 'lifecycle'];
const TAB_LABELS: Record<PipelineType, string> = {
  property: 'Immobilie',
  financing: 'Finanzierung',
  lifecycle: 'Kontakt-Lifecycle',
};

function isPipelineType(value: string | null): value is PipelineType {
  return value !== null && (TYPES as readonly string[]).includes(value);
}

interface NewStageDraft {
  afterStageId: string;
  name: string;
}

/**
 * Admin 03 „Pipelines und Lifecycle": tabs Immobilie / Finanzierung / Kontakt-Lifecycle (`?type=`), the stage
 * table with the drag handle (pointer and arrow keys; system stages stay), inline rename of custom stages,
 * „Stage hinzufügen" as an inline row after the selected row and „Löschen" (Admin 04). „Speichern" sends the new
 * stage (`POST …/stages`) and the order (`PUT …/order`). Read-only tabs show no actions.
 */
export function PipelinesPage() {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const typeParam = params.get('type');
  const type: PipelineType = isPipelineType(typeParam) ? typeParam : 'property';
  const editor = usePipelineEditor(type);
  const addStage = useAddStage();
  const rename = useRenameStage();
  const reorder = useReorderStages();
  const data = editor.data;
  const [order, setOrder] = useState<string[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [newStage, setNewStage] = useState<NewStageDraft | null>(null);
  const [newStageError, setNewStageError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<PipelineItem | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const newInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setOrder(null);
    setNewStage(null);
    setSelectedId(null);
    setBanner(null);
  }, [type]);

  const byId = useMemo(() => new Map((data?.items ?? []).map((item) => [item.id, item])), [data]);
  const serverOrder = useMemo(() => (data?.items ?? []).map((item) => item.id), [data]);
  const currentOrder = useMemo(() => {
    if (!order) return serverOrder;
    // Keep the local order but follow the server's set of stages (a deletion or a new stage elsewhere).
    const known = order.filter((id) => byId.has(id));
    for (const id of serverOrder) if (!known.includes(id)) known.push(id);
    return known;
  }, [order, serverOrder, byId]);
  const orderDirty = order !== null && currentOrder.join() !== serverOrder.join();
  const items = currentOrder.map((id) => byId.get(id)).filter((item): item is PipelineItem => Boolean(item));
  const editable = data?.editable ?? false;
  const canAdd = data?.actions.addStage.allowed ?? false;
  const dirty = orderDirty || (newStage !== null && newStage.name.trim() !== '');

  useEffect(() => {
    if (newStage) newInputRef.current?.focus();
  }, [newStage]);

  function move(id: string, to: number) {
    const list = [...currentOrder];
    const from = list.indexOf(id);
    if (from < 0) return;
    const target = Math.max(0, Math.min(list.length - 1, to));
    if (target === from) return;
    list.splice(from, 1);
    list.splice(target, 0, id);
    setOrder(list);
    const item = byId.get(id);
    if (item) setAnnouncement(`${item.name} steht jetzt an Position ${target + 1}.`);
  }

  function onHandleKeyDown(event: KeyboardEvent<HTMLButtonElement>, item: PipelineItem) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    const index = currentOrder.indexOf(item.id);
    move(item.id, event.key === 'ArrowUp' ? index - 1 : index + 1);
  }

  function onPointerDown(event: PointerEvent<HTMLButtonElement>, item: PipelineItem) {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDraggingId(item.id);
    setSelectedId(item.id);
  }

  function onPointerMove(event: PointerEvent<HTMLButtonElement>, item: PipelineItem) {
    if (draggingId !== item.id) return;
    const y = event.clientY;
    let target = currentOrder.indexOf(item.id);
    currentOrder.forEach((id, index) => {
      const element = rowRefs.current.get(id);
      if (!element || id === item.id) return;
      const box = element.getBoundingClientRect();
      const middle = box.top + box.height / 2;
      const from = currentOrder.indexOf(item.id);
      if (index < from && y < middle) target = Math.min(target, index);
      if (index > from && y > middle) target = Math.max(target, index);
    });
    move(item.id, target);
  }

  function onPointerUp(event: PointerEvent<HTMLButtonElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDraggingId(null);
  }

  function startNewStage() {
    if (!data) return;
    const anchor = selectedId ?? items[items.length - 1]?.id ?? null;
    if (!anchor) return;
    setNewStageError(null);
    setNewStage({ afterStageId: anchor, name: '' });
  }

  async function saveAll() {
    if (!data?.pipelineId) return;
    setBanner(null);
    const pipelineId = data.pipelineId;
    try {
      let fresh = data;
      let createdId: string | null = null;
      if (newStage && newStage.name.trim() !== '') {
        const before = new Set(fresh.items.map((item) => item.id));
        try {
          fresh = await addStage.mutateAsync({
            pipelineId,
            name: newStage.name.trim(),
            afterStageId: newStage.afterStageId,
          });
        } catch (error) {
          setNewStageError(errorMessage(error));
          return;
        }
        createdId = fresh.items.find((item) => !before.has(item.id))?.id ?? null;
        setNewStage(null);
      }
      if (orderDirty) {
        const ids = [...currentOrder];
        if (createdId && newStage) ids.splice(ids.indexOf(newStage.afterStageId) + 1, 0, createdId);
        const known = new Set(fresh.items.map((item) => item.id));
        await reorder.mutateAsync({ pipelineId, stageIds: ids.filter((id) => known.has(id)) });
      }
      setOrder(null);
      toast({ message: 'Pipeline gespeichert.' });
    } catch (error) {
      setBanner(errorMessage(error));
    }
  }

  function saveName(item: PipelineItem, name: string) {
    const trimmed = name.trim();
    if (trimmed === '' || trimmed === item.name) return;
    setBanner(null);
    rename.mutate(
      { stageId: item.id, name: trimmed },
      { onError: (error) => setBanner(errorMessage(error)) },
    );
  }

  const tabs = data?.tabs ?? TYPES.map((key) => ({ key, label: TAB_LABELS[key], active: key === type }));

  return (
    <OrganizationPage
      variant="admin"
      title="Pipelines und Lifecycle"
      description={INTRO}
      actions={
        editable ? (
          <>
            <Button
              variant="secondary"
              disabled={!canAdd || newStage !== null}
              title={data?.actions.addStage.reason ?? undefined}
              onClick={startNewStage}
            >
              Stage hinzufügen
            </Button>
            <Button
              disabled={!dirty}
              loading={addStage.isPending || reorder.isPending}
              onClick={() => void saveAll()}
            >
              Speichern
            </Button>
          </>
        ) : null
      }
    >
      <div className={styles.tabsRow}>
        <div className={styles.tabs} role="tablist" aria-label="Pipeline">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={tab.key === type}
              aria-controls="pipeline-stages"
              className={clsx(styles.tab, tab.key === type && styles.tabActive)}
              onClick={() => setParams(tab.key === 'property' ? {} : { type: tab.key })}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      {editor.isError ? <QueryError error={editor.error} /> : null}
      {banner ? <Banner tone="danger">{banner}</Banner> : null}
      {newStageError ? <Banner tone="danger">{newStageError}</Banner> : null}
      <div id="pipeline-stages" role="tabpanel" aria-label={TAB_LABELS[type]} className={styles.card}>
        <div className={clsx(styles.row, styles.head)} role="presentation">
          <span />
          <span>Nr.</span>
          <span>Stage</span>
          <span>Bedingung</span>
          <span />
          <span />
        </div>
        {editor.isPending
          ? [0, 1, 2, 3, 4].map((index) => (
              <div key={index} className={styles.skeletonRow}>
                <Skeleton width="45%" />
              </div>
            ))
          : null}
        <ol aria-label="Stages" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {items.map((item, index) => {
            const movable = editable && item.actions.move.allowed;
            const renamable = editable && item.actions.rename.allowed;
            return (
              <li key={item.id} aria-label={item.name}>
                <div
                  ref={(element) => {
                    if (element) rowRefs.current.set(item.id, element);
                    else rowRefs.current.delete(item.id);
                  }}
                  className={clsx(
                    styles.row,
                    selectedId === item.id && draggingId === null && styles.rowSelected,
                    draggingId === item.id && styles.rowDragging,
                  )}
                  onClick={() => setSelectedId(item.id)}
                  onFocusCapture={() => setSelectedId(item.id)}
                >
                  {movable ? (
                    <button
                      type="button"
                      className={styles.handle}
                      aria-label={`„${item.name}“ verschieben`}
                      aria-describedby="pipeline-move-help"
                      onKeyDown={(event) => onHandleKeyDown(event, item)}
                      onPointerDown={(event) => onPointerDown(event, item)}
                      onPointerMove={(event) => onPointerMove(event, item)}
                      onPointerUp={onPointerUp}
                      onPointerCancel={onPointerUp}
                    >
                      ⋮⋮
                    </button>
                  ) : editable ? (
                    <span className={styles.handleStatic} aria-hidden="true">
                      ⋮⋮
                    </span>
                  ) : (
                    <span />
                  )}
                  <span className={styles.number}>{index + 1}</span>
                  {renamable ? (
                    <input
                      key={`${item.id}-${item.name}`}
                      aria-label={`Name der Stage ${item.name}`}
                      className={styles.nameInput}
                      defaultValue={item.name}
                      maxLength={60}
                      onBlur={(event) => saveName(item, event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') event.currentTarget.blur();
                        if (event.key === 'Escape') {
                          event.currentTarget.value = item.name;
                          event.currentTarget.blur();
                        }
                      }}
                    />
                  ) : (
                    <span className={styles.name}>{item.name}</span>
                  )}
                  <span className={styles.condition}>{item.condition ?? '—'}</span>
                  <span>{item.system ? <span className={styles.system}>System</span> : null}</span>
                  <span className={styles.actionCell}>
                    {editable && item.actions.delete.allowed ? (
                      <button
                        type="button"
                        className={styles.deleteLink}
                        aria-label={`„${item.name}“ löschen`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeleting(item);
                        }}
                      >
                        Löschen
                      </button>
                    ) : null}
                  </span>
                </div>
                {newStage?.afterStageId === item.id ? (
                  <div className={styles.row}>
                    <span />
                    <span className={styles.number}>{index + 2}</span>
                    <input
                      ref={newInputRef}
                      aria-label="Name der neuen Stage"
                      className={clsx(styles.nameInput, styles.newInput)}
                      value={newStage.name}
                      maxLength={60}
                      placeholder="Name der Stage"
                      onChange={(event) => setNewStage({ ...newStage, name: event.target.value })}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') void saveAll();
                        if (event.key === 'Escape') setNewStage(null);
                      }}
                    />
                    <span className={styles.condition}>—</span>
                    <span />
                    <span className={styles.actionCell}>
                      <button type="button" className={styles.deleteLink} onClick={() => setNewStage(null)}>
                        Verwerfen
                      </button>
                    </span>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      </div>
      <p id="pipeline-move-help" className={styles.srOnly}>
        Mit Pfeil nach oben und unten verschieben. Speichern übernimmt die Reihenfolge.
      </p>
      <p className={styles.srOnly} aria-live="polite">
        {announcement}
      </p>
      <DeleteStageDialog stage={deleting} onClose={() => setDeleting(null)} />
    </OrganizationPage>
  );
}
